import React, { useState, useEffect } from 'react';
import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Button, Tooltip, MenuItem, CircularProgress } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Cookies from 'js-cookie';
import { toast } from "react-toastify";
import api from '../utils/axiosInstance';

const pages = [
  { name: 'Listings', path: '/listings' },
  { name: 'Agencies', path: '/agencies' },
  { name: 'Add Property', path: '/add-property' }
];

const settings = [
  { name: 'Profile', path: '/profile' },
];

const apiUrl = process.env.REACT_APP_API_URL;

function Header() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false); // Loading state for logout
  const navigate = useNavigate();

  useEffect(() => {
    const userCookie = Cookies.get('user');
    if (userCookie) {
      const user = JSON.parse(userCookie);
      setUserData(user);
      setIsLoggedIn(true);
    }

    const handleUserLoggedOut = () => {
      setIsLoggedIn(false);
      setUserData(null);
    };

    window.addEventListener('userLoggedOut', handleUserLoggedOut);
    return () => {
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
    };
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    setLogoutLoading(true); // Start loading
    api.post(`${apiUrl}/api/v1/auth/logout/`)
      .then((response) => {
        Cookies.remove('user', { path: '/', sameSite: 'None', secure: true });
        Cookies.remove('access', { path: '/', sameSite: 'None', secure: true });
        Cookies.remove('refresh', { path: '/', sameSite: 'None', secure: true });
        Cookies.remove('csrftoken', { path: '/', sameSite: 'None', secure: true });

        setIsLoggedIn(false);
        setUserData(null);
        setAnchorElUser(null);
        navigate('/');

        const successMessage = response.data.message || "Logout successful.";
        toast.success(successMessage);
      })
      .catch(error => {
        console.error("Logout failed", error);
        let errorMessage = "Logout failed. Please try again.";
        if (error.response && error.response.data) {
          errorMessage = error.response.data.detail || errorMessage;
        }
        toast.error(errorMessage);
      })
      .finally(() => {
        setLogoutLoading(false); // End loading
      });
  };

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <IconButton
              component={Link}
              to="/"
              sx={{ mr: 1 }}
              color="inherit"
            >
              <HomeIcon />
            </IconButton>
            <IconButton
              size="large"
              aria-label="open navigation"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <IconButton
            component={Link}
            to="/"
            sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }}
            color="inherit"
          >
            <HomeIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            PROPARTY
          </Typography>

          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            {pages.map((page) => (
              <MenuItem key={page.name} onClick={handleCloseNavMenu}>
                <Typography textAlign="center">
                  <Link to={page.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                    {page.name}
                  </Link>
                </Typography>
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button
                key={page.name}
                component={Link}
                to={page.path}
                sx={{ my: 2, color: 'white', display: 'block', fontWeight: 'bold', fontSize: '1rem' }}
              >
                {page.name}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            {isLoggedIn ? (
              <>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, color: 'inherit' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      {userData?.names || 'User'} <ArrowDropDownIcon />
                    </Typography>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map((setting) => (
                    <MenuItem
                      key={setting.name}
                      onClick={() => {
                        navigate(setting.path);
                        handleCloseUserMenu();
                      }}
                    >
                      <Typography textAlign="center">{setting.name}</Typography>
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleLogout}>
                    <Typography textAlign="center">
                      {logoutLoading ? <CircularProgress size={20} color="inherit" /> : "Logout"}
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button color="inherit" onClick={() => navigate('/login')}>Login</Button>
                <Button color="inherit">
                  <Link to="/signup" style={{ textDecoration: 'none', color: 'inherit' }}>Sign Up</Link>
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Header;

// worked perfectly just neede to add loading state to the logout button
// import React, { useState, useEffect } from 'react';
// import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Button, Tooltip, MenuItem } from '@mui/material';
// import { Link, useNavigate } from 'react-router-dom';
// import MenuIcon from '@mui/icons-material/Menu';
// import HomeIcon from '@mui/icons-material/Home';
// import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
// import Cookies from 'js-cookie';
// import { toast } from "react-toastify";
// import api from '../utils/axiosInstance';

// const pages = [
//   { name: 'Listings', path: '/listings' },
//   { name: 'Agencies', path: '/agencies' },
//   { name: 'Add Property', path: '/add-property' }
// ];

// const settings = [
//   { name: 'Profile', path: '/profile' },
//   // { name: 'Account', path: '/account' },
//   // { name: 'Dashboard', path: '/dashboard' },
// ];
// const apiUrl = process.env.REACT_APP_API_URL;

// function Header() {
//   const [anchorElNav, setAnchorElNav] = useState(null);
//   const [anchorElUser, setAnchorElUser] = useState(null);
//   const [isLoggedIn, setIsLoggedIn] = useState(false);
//   const [userData, setUserData] = useState(null);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const userCookie = Cookies.get('user');
//     if (userCookie) {
//       const user = JSON.parse(userCookie);
//       setUserData(user);
//       setIsLoggedIn(true);
//     }

//     const handleUserLoggedOut = () => {
//       setIsLoggedIn(false);
//       setUserData(null);
//     };

//     window.addEventListener('userLoggedOut', handleUserLoggedOut);
//     return () => {
//       window.removeEventListener('userLoggedOut', handleUserLoggedOut);
//     };
//   }, []);

//   const handleOpenNavMenu = (event) => {
//     setAnchorElNav(event.currentTarget);
//   };

//   const handleOpenUserMenu = (event) => {
//     setAnchorElUser(event.currentTarget);
//   };

//   const handleCloseNavMenu = () => {
//     setAnchorElNav(null);
//   };

//   const handleCloseUserMenu = () => {
//     setAnchorElUser(null);
//   };
  
//   // chatgpt to help clear out cookies was working but not removing cookies from browser although user can login
//   const handleLogout = () => {
//     api.post(`${apiUrl}/api/v1/auth/logout/`)
//       .then((response) => {
//         // Clear cookies using js-cookie
//         Cookies.remove('user', { path: '/', sameSite: 'None', secure: true });
//         Cookies.remove('access', { path: '/', sameSite: 'None', secure: true  });
//         Cookies.remove('refresh', { path: '/', sameSite: 'None', secure: true  });
//         // Cookies.remove('csrftoken', { sameSite: 'None', secure: true }, { path: '/' });
//         Cookies.remove('csrftoken', { path: '/', sameSite: 'None', secure: true });
      
//         // Additional: Clear any other cookies manually if needed
//         document.cookie.split(";").forEach((cookie) => {
//           const eqPos = cookie.indexOf("=");
//           const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
//           document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
//         });
//         // document.cookie.split(";").forEach((cookie) => {
//         //   const name = cookie.split("=")[0].trim();
//         //   document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
//         // }); 

//         setIsLoggedIn(false);
//         setUserData(null);
//         setAnchorElUser(null);
//         navigate('/');

//         const successMessage = response.data.message || "Logout successful.";
//         toast.success(successMessage);
//       })
//       .catch(error => {
//         console.error("Logout failed", error);
//         let errorMessage = "Logout failed. Please try again.";
//         if (error.response && error.response.data) {
//           errorMessage = error.response.data.detail || errorMessage;
//         }
//         toast.error(errorMessage);
//       });
//   };

//   // the one i was using but cookies were not clearig from Cookie Storage upon logout
//   // const handleLogout = () => {
//   //   // api.post('auth/logout/')
//   //   api.post(`${apiUrl}/api/v1/auth/logout/`)
//   //     .then((response) => {
//   //       setIsLoggedIn(false);
//   //       setUserData(null);
//   //       Cookies.remove('user');
//   //       Cookies.remove('access');
//   //       Cookies.remove('refresh');
//   //       Cookies.remove('csrftoken', { sameSite: 'Lax' });
//   //       setAnchorElUser(null);
//   //       navigate('/');

//   //       const successMessage = response.data.message || "Logout successful.";
//   //       toast.success(successMessage);
//   //     })
//   //     .catch(error => {
//   //       console.error("Logout failed", error);
//   //       let errorMessage = "Logout failed. Please try again.";
//   //       if (error.response && error.response.data) {
//   //         errorMessage = error.response.data.detail || errorMessage;
//   //       }
//   //       toast.error(errorMessage);
//   //     });
//   // };

//   return (
//     <AppBar position="static">
//       <Container maxWidth="xl">
//         <Toolbar disableGutters>

//           {/* Home Icon and Mobile Menu */}
//           <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
//             <IconButton
//               component={Link}
//               to="/"
//               sx={{ mr: 1 }}
//               color="inherit"
//             >
//               <HomeIcon />
//             </IconButton>
//             <IconButton
//               size="large"
//               aria-label="open navigation"
//               aria-controls="menu-appbar"
//               aria-haspopup="true"
//               onClick={handleOpenNavMenu}
//               color="inherit"
//             >
//               <MenuIcon />
//             </IconButton>
//           </Box>

//           {/* Desktop Home Icon */}
//           <IconButton
//             component={Link}
//             to="/"
//             sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }}
//             color="inherit"
//           >
//             <HomeIcon />
//           </IconButton>
//           <Typography
//             variant="h6"
//             noWrap
//             component={Link}
//             to="/"
//             sx={{
//               mr: 2,
//               display: { xs: 'none', md: 'flex' },
//               fontFamily: 'monospace',
//               fontWeight: 700,
//               letterSpacing: '.3rem',
//               color: 'inherit',
//               textDecoration: 'none',
//             }}
//           >
//             PROPARTY
//           </Typography>

//           {/* Mobile Menu */}
//           <Menu
//             id="menu-appbar"
//             anchorEl={anchorElNav}
//             anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
//             keepMounted
//             transformOrigin={{ vertical: 'top', horizontal: 'left' }}
//             open={Boolean(anchorElNav)}
//             onClose={handleCloseNavMenu}
//             sx={{ display: { xs: 'block', md: 'none' } }}
//           >
//             {pages.map((page) => (
//               <MenuItem key={page.name} onClick={handleCloseNavMenu}>
//                 <Typography textAlign="center">
//                   <Link to={page.path} style={{ textDecoration: 'none', color: 'inherit' }}>
//                     {page.name}
//                   </Link>
//                 </Typography>
//               </MenuItem>
//             ))}
//           </Menu>

//           {/* Desktop Menu */}
//           <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
//             {pages.map((page) => (
//               <Button
//                 key={page.name}
//                 component={Link}
//                 to={page.path}
//                 sx={{ my: 2, color: 'white', display: 'block', fontWeight: 'bold', fontSize: '1rem' }}
//               >
//                 {page.name}
//               </Button>
//             ))}
//           </Box>

//           {/* User Menu */}
//           <Box sx={{ flexGrow: 0 }}>
//             {isLoggedIn ? (
//               <>
//                 <Tooltip title="Open settings">
//                   <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, color: 'inherit' }}>
//                     <Typography variant="body1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
//                       {userData?.names || 'User'} <ArrowDropDownIcon />
//                     </Typography>
//                   </IconButton>
//                 </Tooltip>
//                 <Menu
//                   sx={{ mt: '45px' }}
//                   id="menu-appbar"
//                   anchorEl={anchorElUser}
//                   anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
//                   keepMounted
//                   transformOrigin={{ vertical: 'top', horizontal: 'right' }}
//                   open={Boolean(anchorElUser)}
//                   onClose={handleCloseUserMenu}
//                 >
//                   {settings.map((setting) => (
//                     <MenuItem
//                       key={setting.name}
//                       onClick={() => {
//                         navigate(setting.path);
//                         handleCloseUserMenu();
//                       }}
//                     >
//                       <Typography textAlign="center">{setting.name}</Typography>
//                     </MenuItem>
//                   ))}
//                   <MenuItem onClick={handleLogout}>
//                     <Typography textAlign="center">Logout</Typography>
//                   </MenuItem>
//                 </Menu>
//               </>
//             ) : (
//               <Box sx={{ display: 'flex', gap: 2 }}>
//                 <Button color="inherit" onClick={() => navigate('/login')}>Login</Button>
//                 <Button color="inherit">
//                   <Link to="/signup" style={{ textDecoration: 'none', color: 'inherit' }}>Sign Up</Link>
//                 </Button>
//               </Box>
//             )}
//           </Box>
//         </Toolbar>
//       </Container>
//     </AppBar>
//   );
// }

// export default Header;

// // worked and the colors were perfect just that hovering the user fullname is awful
// import React, { useState, useEffect } from 'react';
// import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Button, Tooltip, MenuItem } from '@mui/material';
// import { Link, useNavigate } from 'react-router-dom';
// import MenuIcon from '@mui/icons-material/Menu';
// import HomeIcon from '@mui/icons-material/Home';
// import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
// import Cookies from 'js-cookie';
// import { toast } from "react-toastify";
// import api from '../utils/axiosInstance';

// const pages = [
//   { name: 'Listings', path: '/listings' },
//   { name: 'Agencies', path: '/agencies' },
//   { name: 'Add Property', path: '/add-property' }
// ];

// const settings = [
//   { name: 'Profile', path: '/profile' },
//   { name: 'Account', path: '/account' },
//   { name: 'Dashboard', path: '/dashboard' },
// ];

// function Header() {
//   const [anchorElNav, setAnchorElNav] = useState(null);
//   const [anchorElUser, setAnchorElUser] = useState(null);
//   const [isLoggedIn, setIsLoggedIn] = useState(false);
//   const [userData, setUserData] = useState(null);
//   const navigate = useNavigate(); 

//   useEffect(() => {
//     const userCookie = Cookies.get('user');
//     if (userCookie) {
//       const user = JSON.parse(userCookie);
//       setUserData(user);
//       setIsLoggedIn(true);
//     }

//     const handleUserLoggedOut = () => {
//       setIsLoggedIn(false);
//       setUserData(null);
//     };

//     window.addEventListener('userLoggedOut', handleUserLoggedOut);
//     return () => {
//       window.removeEventListener('userLoggedOut', handleUserLoggedOut);
//     };
//   }, []);

//   const handleOpenNavMenu = (event) => {
//     setAnchorElNav(event.currentTarget);
//   };

//   const handleOpenUserMenu = (event) => {
//     setAnchorElUser(event.currentTarget);
//   };

//   const handleCloseNavMenu = () => {
//     setAnchorElNav(null);
//   };

//   const handleCloseUserMenu = () => {
//     setAnchorElUser(null);
//   };

//   const handleLogout = () => {
//     api.post('auth/logout/')
//       .then((response) => {
//         setIsLoggedIn(false);
//         setUserData(null);
//         Cookies.remove('user');
//         Cookies.remove('access');
//         Cookies.remove('refresh');
//         Cookies.remove('csrftoken', { sameSite: 'Lax' });
//         setAnchorElUser(null);
//         navigate('/');

//         const successMessage = response.data.message || "Logout successful.";
//         toast.success(successMessage);
//       })
//       .catch(error => {
//         console.error("Logout failed", error);
//         let errorMessage = "Logout failed. Please try again.";
//         if (error.response && error.response.data) {
//           errorMessage = error.response.data.detail || errorMessage;
//         }
//         toast.error(errorMessage);
//       });
//   };

//   return (
//     <AppBar position="static">
//       <Container maxWidth="xl">
//         <Toolbar disableGutters>
//           <HomeIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
//           <Typography
//             variant="h6"
//             noWrap
//             component="a"
//             href="/"
//             sx={{
//               mr: 2,
//               display: { xs: 'none', md: 'flex' },
//               fontFamily: 'monospace',
//               fontWeight: 700,
//               letterSpacing: '.3rem',
//               color: 'inherit',
//               textDecoration: 'none',
//             }}
//           >
//             PROPARTY
//           </Typography>

//           {/* Mobile Menu Icon */}
//           <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
//             <IconButton
//               size="large"
//               aria-label="open navigation"
//               aria-controls="menu-appbar"
//               aria-haspopup="true"
//               onClick={handleOpenNavMenu}
//               color="inherit"
//             >
//               <MenuIcon />
//             </IconButton>
//             <Menu
//               id="menu-appbar"
//               anchorEl={anchorElNav}
//               anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
//               keepMounted
//               transformOrigin={{ vertical: 'top', horizontal: 'left' }}
//               open={Boolean(anchorElNav)}
//               onClose={handleCloseNavMenu}
//               sx={{ display: { xs: 'block', md: 'none' } }}
//             >
//               {pages.map((page) => (
//                 <MenuItem key={page.name} onClick={handleCloseNavMenu}>
//                   <Typography textAlign="center">
//                     <Link to={page.path} style={{ textDecoration: 'none', color: 'inherit' }}>
//                       {page.name}
//                     </Link>
//                   </Typography>
//                 </MenuItem>
//               ))}
//             </Menu>
//           </Box>

//           {/* Desktop Menu */}
//           <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
//             {pages.map((page) => (
//               <Button
//                 key={page.name}
//                 onClick={handleCloseNavMenu}
//                 sx={{ my: 2, color: 'white', display: 'block', fontWeight: 'bold', fontSize: '1rem' }}
//               >
//                 <Link to={page.path} style={{ textDecoration: 'none', color: 'inherit' }}>
//                   {page.name}
//                 </Link>
//               </Button>
//             ))}
//           </Box>

//           {/* User Menu */}
//           <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
//             {isLoggedIn ? (
//               <>
//                 <Tooltip title="Open settings">
//                   <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, color: 'inherit' }}>
//                     <Typography variant="body1" sx={{ fontWeight: 'bold', textTransform: 'capitalize', display: 'flex', alignItems: 'center' }}>
//                       {userData?.names || 'User'} <ArrowDropDownIcon />
//                     </Typography>
//                   </IconButton>
//                 </Tooltip>
//                 <Menu
//                   sx={{ mt: '45px' }}
//                   id="menu-appbar"
//                   anchorEl={anchorElUser}
//                   anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
//                   keepMounted
//                   transformOrigin={{ vertical: 'top', horizontal: 'right' }}
//                   open={Boolean(anchorElUser)}
//                   onClose={handleCloseUserMenu}
//                 >
//                   {settings.map((setting) => (
//                     <MenuItem
//                       key={setting.name}
//                       onClick={() => {
//                         navigate(setting.path);
//                         handleCloseUserMenu();
//                       }}
//                     >
//                       <Typography textAlign="center">{setting.name}</Typography>
//                     </MenuItem>
//                   ))}
//                   <MenuItem onClick={handleLogout}>
//                     <Typography textAlign="center">Logout</Typography>
//                   </MenuItem>
//                 </Menu>
//               </>
//             ) : (
//               <Box sx={{ display: 'flex', gap: 2 }}>
//                 <Button color="inherit" onClick={() => navigate('/login')}>Login</Button>
//                 <Button color="inherit">
//                   <Link to="/signup" style={{ textDecoration: 'none', color: 'inherit' }}>Sign Up</Link>
//                 </Button>
//               </Box>
//             )}
//           </Box>
//         </Toolbar>
//       </Container>
//     </AppBar>
//   );
// }

// export default Header;